<template>
  <div class="news-management card">
    <!-- Header -->
    <div class="card-header d-flex align-items-center justify-content-between">
      <h4 class="card-title mb-0">
        <i class="bi bi-newspaper me-2"></i>News Management
      </h4>
      <div class="d-flex gap-2">
        <button class="btn btn-primary" @click="showAddModal">
          <i class="bi bi-plus me-2"></i>Add News
        </button>
      </div>
    </div>

    <!-- Filters -->
    <div class="card-body">
<!--      <div class="row mb-4">-->
<!--        <div class="col-md-4">-->
<!--          <label class="form-label">Search</label>-->
<!--          <div class="input-group">-->
<!--            <input-->
<!--              v-model="filters.search"-->
<!--              type="text"-->
<!--              class="form-control"-->
<!--              placeholder="Search articles..."-->
<!--              @keyup.enter="fetchNews"-->
<!--            >-->
<!--            <button class="btn btn-outline-secondary" @click="clearFilters">-->
<!--              <i class="bi bi-x-circle"></i>-->
<!--            </button>-->
<!--          </div>-->
<!--        </div>-->
<!--        <div class="col-md-3">-->
<!--          <label class="form-label">Date From</label>-->
<!--          <input v-model="filters.date_from" type="date" class="form-control">-->
<!--        </div>-->
<!--        <div class="col-md-3">-->
<!--          <label class="form-label">Date To</label>-->
<!--          <input v-model="filters.date_to" type="date" class="form-control">-->
<!--        </div>-->
<!--        <div class="col-md-2">-->
<!--          <label class="form-label">&nbsp;</label>-->
<!--          <button class="btn btn-primary w-100" @click="fetchNews">-->
<!--            <i class="bi bi-search me-2"></i>Search-->
<!--          </button>-->
<!--        </div>-->
<!--      </div>-->

      <!-- Articles List -->
      <div class="articles-section">
        <div class="d-flex align-items-center justify-content-between mb-3">
          <h5 class="mb-0">Articles</h5>
        </div>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Loading articles...</p>
        </div>

        <div v-else-if="news.length === 0" class="text-center py-5">
          <i class="bi bi-newspaper display-1 text-muted"></i>
          <h5 class="mt-3 text-muted">No articles found</h5>
          <p class="text-muted">Create your first news article to get started.</p>
        </div>

        <div v-else>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Author</th>
                <th>Created</th>
                <th width="150">Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="article in news" :key="article.id">
                <td>
                  <div class="d-flex flex-column">
                    <strong class="text-truncate" style="max-width: 300px;">
                      {{ article.title || 'No Title' }}
                    </strong>
                    <small class="text-muted">
                      {{ truncateContent(article.content || '', 80) }}
                    </small>
                  </div>
                </td>
                <td>
                  <span class="badge bg-light text-dark">
                    {{ article.date ? formatDate(article.date) : 'No Date' }}
                  </span>
                </td>
                <td>{{ article.author?.name || 'Unknown' }}</td>
                <td>{{ article.created_at ? formatDateTime(article.created_at) : 'No Date' }}</td>
                <td>
                  <div class="dropdown">
                    <button
                      class="btn btn-sm btn-outline-secondary dropdown-toggle"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      <i class="bi bi-three-dots"></i>
                    </button>
                    <ul class="dropdown-menu">
                      <li><a class="dropdown-item" href="#" @click="viewArticle(article)">View Article</a></li>
                      <li><a class="dropdown-item" href="#" @click="editArticle(article)">Edit</a></li>
                      <li><hr class="dropdown-divider"></li>
                      <li><a class="dropdown-item text-danger" href="#" @click="showDeleteModal(article)">Delete</a></li>
                    </ul>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center mt-4">
            <div class="text-muted">
              Showing {{ ((pagination.current_page - 1) * pagination.per_page) + 1 }} to
              {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
              of {{ pagination.total }} results
            </div>
            <nav v-if="pagination.total_pages > 1">
              <ul class="pagination mb-0">
                <li class="page-item" :class="{ disabled: pagination.current_page === 1 }">
                  <button class="page-link" @click="goToPage(pagination.current_page - 1)">Previous</button>
                </li>
                <li
                  v-for="page in paginationPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: page === pagination.current_page }"
                >
                  <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                </li>
                <li class="page-item" :class="{ disabled: pagination.current_page === pagination.total_pages }">
                  <button class="page-link" @click="goToPage(pagination.current_page + 1)">Next</button>
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Add News Modal -->
    <div v-if="showModal" class="modal-overlay" @click.stop>
      <div class="modal-container">
        <div class="modal-header">
          <h5 class="modal-title">{{ editingArticle ? 'Edit Article' : 'Add New Article' }}</h5>
          <button type="button" class="close-btn" @click="closeModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <form @submit.prevent="saveArticle">
          <div class="modal-body">
            <div class="form-group">
              <label class="form-label">Title <span class="text-danger">*</span></label>
              <input
                v-model="articleForm.title"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.title }"
                placeholder="Stay up to date title"
                required
              >
              <div v-if="errors.title" class="invalid-feedback">{{ errors.title[0] }}</div>
            </div>

            <div class="form-group">
              <label class="form-label">Content <span class="text-danger">*</span></label>
              <RichTextEditor v-model="articleForm.content" :invalid="!!errors.content" />
              <div v-if="errors.content" class="invalid-feedback">{{ errors.content[0] }}</div>
            </div>

            <div class="form-group">
              <label class="form-label" style="z-index: 2;">Upload Featured Image</label>
              <FileDropZone
                class="file-upload-area"
                :multiple="false"
                accept="image/*"
                @files="handleFeaturedImageFiles"
                v-slot="{ isDragOver }"
              >
                <div
                  class="file-upload-content"
                  :class="{ 'is-invalid': errors.featured_image, 'file-upload-content--drag': isDragOver }"
                >
                  <div v-if="!articleForm.featured_image && !imagePreview" class="upload-placeholder">
                    <i class="bi bi-cloud-upload upload-icon"></i>
                    <p class="upload-text">Choose file</p>
                    <p class="upload-subtext">Drag and drop or click to select image</p>
                    <p class="upload-hint">Supports: JPEG, PNG, GIF, WebP (Max 5MB)</p>
                  </div>
                  <div v-else class="uploaded-file">
                    <div v-if="imagePreview" class="image-preview">
                      <img :src="imagePreview" alt="Preview" class="preview-image">
                      <button type="button" class="remove-image" @click.stop="removeImage">
                        <i class="bi bi-x-lg"></i>
                      </button>
                    </div>
                    <div v-else class="file-info">
                      <i class="bi bi-file-earmark-image"></i>
                      <span>{{ articleForm.featured_image?.name || 'Featured image' }}</span>
                    </div>
                  </div>
                </div>
              </FileDropZone>
              <div v-if="errors.featured_image" class="invalid-feedback">{{ errors.featured_image[0] }}</div>
            </div>

            <div class="form-group">
              <label class="form-label">Date <span class="text-danger">*</span></label>
              <input
                v-model="articleForm.date"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.date }"
                required
              >
              <div v-if="errors.date" class="invalid-feedback">{{ errors.date[0] }}</div>
            </div>

            <div class="form-group">
              <div class="form-check-container" style="padding-top: 2rem; padding-bottom: 0rem;">
                <div class="toggle-switch">
                  <input
                    id="featureToggle"
                    v-model="articleForm.is_featured"
                    type="checkbox"
                    class="toggle-input"
                  >
                  <label for="featureToggle" class="toggle-label">
                    <span class="toggle-slider"></span>
                  </label>
                </div>
                <label for="featureToggle" class="toggle-text">Feature selected article</label>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">Cancel</button>
            <button type="submit" class="btn btn-primary" :disabled="saving">
              <span v-if="saving" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingArticle ? 'Update' : 'Create' }} Article
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- View Modal -->
    <div v-if="showViewModal" class="modal-overlay" @click.stop>
      <div class="modal-container modal-lg">
        <div class="modal-header">
          <h5 class="modal-title">{{ viewingArticle?.title }}</h5>
          <button type="button" class="close-btn" @click="closeViewModal">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="viewingArticle">
            <div class="mb-3">
              <small class="text-muted">
                <strong>Date:</strong> {{ formatDate(viewingArticle.date) }} |
                <strong>Author:</strong> {{ viewingArticle.author?.name || 'Unknown' }} |
                <strong>Created:</strong> {{ formatDateTime(viewingArticle.created_at) }}
              </small>
            </div>
            <div class="article-content rich-content">
              <div v-html="formatContent(viewingArticle.content)"></div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeViewModal">Close</button>
          <button type="button" class="btn btn-primary" @click="editFromView">
            <i class="bi bi-pencil me-2"></i>Edit Article
          </button>
        </div>
      </div>
    </div>
    <div v-if="deleteConfirm.show" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h5 class="modal-title">Delete Article</h5>
          <button type="button" class="close-btn" @click="deleteConfirm.show = false">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <div class="modal-body">
          <p>Are you sure you want to delete:</p>
          <p><strong>"{{ deleteConfirm.article?.title }}"</strong>?</p>
          <p class="text-danger mt-2">This action cannot be undone.</p>
        </div>

        <div class="modal-footer">
          <button class="btn btn-secondary" @click="deleteConfirm.show = false">Cancel</button>
          <button class="btn btn-danger" @click="confirmDelete">Delete</button>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>

    <!-- Featured Image Cropper -->
    <CoverImageCropper
      v-model="showImageCropper"
      :imageSrc="imageCropperSrc"
      title="Set Featured Image"
      @cropped="handleImageCropped"
      @cancel="handleImageCropperCancel"
    />
  </div>
</template>

<script>
import NewsApiService from '@/services/news/NewsApiServices'
import CoverImageCropper from '@/components/common/CoverImageCropper.vue'
import RichTextEditor from '@/components/shared/RichTextEditor.vue'
import FileDropZone from '@/components/common/FileDropZone.vue'

export default {
  name: 'AdminNews',

  components: {
    CoverImageCropper,
    RichTextEditor,
    FileDropZone
  },

  data() {
    return {
      news: [],
      loading: false,
      saving: false,
      selectedArticles: [],
      editingArticle: null,
      viewingArticle: null,
      showModal: false,
      showViewModal: false,
      filters: {
        search: '',
        date_from: '',
        date_to: ''
      },
      articleForm: {
        title: '',
        content: '',
        date: '',
        featured_image: null,
        is_featured: false
      },
      imagePreview: null,
      showImageCropper: false,
      imageCropperSrc: null,
      errors: {},
      pagination: {
        current_page: 1,
        total_pages: 1,
        per_page: 15,
        total: 0
      },
      deleteConfirm: {
        show: false,
        article: null
      },
      toast: {
        show: false,
        type: 'success',
        message: ''
      },
    }
  },

  computed: {
    selectAll() {
      return this.news.length > 0 && this.selectedArticles.length === this.news.length
    },

    paginationPages() {
      const pages = []
      const current = this.pagination.current_page
      const total = this.pagination.total_pages

      let start = Math.max(1, current - 2)
      let end = Math.min(total, start + 4)

      if (end - start < 4) {
        start = Math.max(1, end - 4)
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      return pages
    }
  },

  mounted() {
    this.fetchNews()
    this.setDefaultDate()
  },

  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    showDeleteModal(article) {
      this.deleteConfirm.show = true
      this.deleteConfirm.article = article
    },

    async fetchNews(page = 1) {
      this.loading = true
      try {
        const params = NewsApiService.buildSearchParams({
          page,
          per_page: this.pagination.per_page,
          ...this.filters
        })

        const response = await NewsApiService.getAdminNews(params)

        if (response && response.success) {
          // FIX: Extract the actual articles array from the nested structure
          this.news = response.data.data || []  // <-- THIS IS THE KEY CHANGE

          // Handle pagination from the nested structure
          if (response.data.pagination) {
            this.pagination = response.data.pagination
          } else {
            this.pagination = {
              current_page: 1,
              total_pages: 1,
              per_page: 15,
              total: this.news.length
            }
          }
        } else {
          this.news = []
        }
      } catch (error) {
        console.error('Error:', error);
        this.news = []
      } finally {
        this.loading = false
      }
    },

    showAddModal() {
      this.editingArticle = null
      this.resetForm()
      this.setDefaultDate()
      this.showModal = true
    },

    // Fixed editArticle method
    editArticle(article) {
      this.editingArticle = article
      this.articleForm = {
        title: article.title,
        content: article.content,
        date: article.date,
        featured_image: null,
        is_featured: article.is_featured || false
      }
      this.errors = {}
      this.imagePreview = article.featured_image_url || null
      this.showModal = true
    },

    viewArticle(article) {
      this.viewingArticle = article
      this.showViewModal = true
    },

    editFromView() {
      this.showViewModal = false
      setTimeout(() => {
        this.editArticle(this.viewingArticle)
      }, 300)
    },

    async saveArticle() {
      this.saving = true;
      this.errors = {};

      try {
        // Client-side validation
        const validationErrors = {};

        if (!this.articleForm.title || this.articleForm.title.trim().length === 0) {
          validationErrors.title = ['Title is required'];
        }

        if (!this.articleForm.content ||
          this.articleForm.content.trim().length === 0 ||
          this.articleForm.content === '<br>' ||
          this.articleForm.content === '<p></p>' ||
          this.articleForm.content === '<p><br></p>' ||
          this.articleForm.content === '<div><br></div>') {
          validationErrors.content = ['Content is required'];
        }

        if (!this.articleForm.date) {
          validationErrors.date = ['Date is required'];
        }

        if (Object.keys(validationErrors).length > 0) {
          this.errors = validationErrors;
          this.showToast('error','Please fill in all required fields');
          this.saving = false;
          return;
        }

        // Prepare clean data object - handle file properly
        const dataToSubmit = {
          title: this.articleForm.title.trim(),
          content: this.articleForm.content.trim(),
          date: this.articleForm.date,
          is_featured: Boolean(this.articleForm.is_featured)
        };

        // IMPORTANT: Only include featured_image if it's actually a File object
        if (this.articleForm.featured_image &&
          this.articleForm.featured_image instanceof File &&
          this.articleForm.featured_image.size > 0) {
          dataToSubmit.featured_image = this.articleForm.featured_image;
        } else {
          console.log('Not including image - no valid file');
        }

        let response;
        if (this.editingArticle) {
          response = await NewsApiService.updateNews(this.editingArticle.id, dataToSubmit);
        } else {
          response = await NewsApiService.createNews(dataToSubmit);
        }

        if (response && response.success) {
          this.showToast('success', `Article ${this.editingArticle ? 'updated' : 'created'} successfully!`);
          this.closeModal();
          await this.fetchNews(this.pagination.current_page);
        } else {
          console.error('API call failed:', response);
          if (response && response.errors) {
            this.errors = response.errors;
          }
          this.showToast('error',response?.message || 'Failed to save article');
        }

      } catch (error) {
        console.error('Save error details:', {
          message: error.message,
          response: error.response,
          stack: error.stack
        });

        if (error.response && error.response.status === 422) {
          this.errors = error.response.data.errors || {};
          this.showToast('error','Validation failed - please check the form');
        } else {
          this.showToast('error','An unexpected error occurred while saving');
        }
      } finally {
        this.saving = false;
      }
    },

    async deleteArticle(article) {
      if (!confirm(`Are you sure you want to delete "${article.title}"?`)) return

      try {
        const response = await NewsApiService.deleteNews(article.id)

        if (response?.data?.success) {
          this.showToast('success', 'Article deleted successfully!')
          await this.fetchNews(this.pagination.current_page)
        } else {
          this.showToast('error',response?.data?.message || 'Failed to delete article')
        }

      } catch (error) {
        this.showToast('error','Failed to delete article')
        console.error('Delete error:', error)
      }
    },

    async confirmDelete() {
      const article = this.deleteConfirm.article

      try {
        const response = await NewsApiService.deleteNews(article.id)

        const success =
          response?.success ||
          response?.data?.success ||
          response?.data?.data?.success ||
          response === true ||
          response?.status === 'success';

        if (success) {
          this.showToast('success', 'Article deleted successfully!');
          this.deleteConfirm.show = false;
          this.deleteConfirm.article = null;
          await this.refreshAfterDelete();
        } else {
          this.showToast('error', 'Failed to delete article');
        }


      } catch (error) {
        this.showToast('error', 'Failed to delete article')
      }
    },

    async refreshAfterDelete() {
      // If the page now has 0 items, go back one page (if not already on page 1)
      if (this.news.length === 0 && this.pagination.current_page > 1) {
        await this.fetchNews(this.pagination.current_page - 1)
      } else {
        await this.fetchNews(this.pagination.current_page)
      }
    },

    async bulkDelete() {
      if (this.selectedArticles.length === 0) return

      if (!confirm(`Are you sure you want to delete ${this.selectedArticles.length} selected articles?`)) return

      try {
        const response = await NewsApiService.bulkDeleteNews(this.selectedArticles)

        if (response.success) {
          this.showToast('success', `${this.selectedArticles.length} articles deleted successfully!`)
          this.selectedArticles = []
          await this.fetchNews(this.pagination.current_page)
        } else {
          this.showToast('error',response.message || 'Failed to delete articles')
        }
      } catch (error) {
        this.showToast('error','Failed to delete articles')
        console.error('Error:', error)
      }
    },

    toggleSelectAll() {
      if (this.selectAll) {
        this.selectedArticles = []
      } else {
        this.selectedArticles = this.news.map(article => article.id)
      }
    },

    goToPage(page) {
      if (page >= 1 && page <= this.pagination.total_pages) {
        this.fetchNews(page)
      }
    },

    clearFilters() {
      this.filters = {
        search: '',
        date_from: '',
        date_to: ''
      }
      this.fetchNews(1)
    },

    resetForm() {
      this.articleForm = {
        title: '',
        content: '',
        date: '',
        featured_image: null, // Use null instead of empty object
        is_featured: false
      }
      this.imagePreview = null
      this.errors = {}
    },

    setDefaultDate() {
      const today = new Date().toISOString().split('T')[0]
      if (!this.articleForm.date) {
        this.articleForm.date = today
      }
    },

    closeModal() {
      this.showModal = false
      this.resetForm()
      this.editingArticle = null
    },

    closeViewModal() {
      this.showViewModal = false
      this.viewingArticle = null
    },

    formatDate(dateString) {
      if (!dateString) return 'No Date';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid Date';

        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'No Date';

      try {
        const date = new Date(dateTimeString);
        if (isNaN(date.getTime())) return 'Invalid Date';

        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return 'Invalid Date';
      }
    },

    truncateContent(content, maxLength = 100) {
      if (!content) return 'No content';

      // Strip HTML tags for display, replacing them with a space (not an empty
      // string) so adjacent block-level elements (e.g. <li> items) don't get
      // glued together into one word, then collapse the resulting whitespace.
      const textContent = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
      return textContent.length <= maxLength
        ? textContent
        : textContent.substring(0, maxLength) + '...';
    },

    formatContent(content) {
      return content.replace(/\n/g, '<br>')
    },

    // Picked through the browse dialog or dropped on the upload area.
    handleFeaturedImageFiles(files) {
      const file = files[0]
      if (!file) return

      if (!file.type.startsWith('image/')) {
        this.errors = { ...this.errors, featured_image: ['Please choose an image file (JPEG, PNG, GIF or WebP)'] }
        return
      }

      const otherErrors = { ...this.errors }
      delete otherErrors.featured_image
      this.errors = otherErrors
      this.handleFileUpload(file)
    },

    handleFileUpload(file) {
      if (!file) return

      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageCropperSrc = e.target.result
        this.showImageCropper = true
      }
      reader.readAsDataURL(file)
    },

    handleImageCropped(base64) {
      // Convert the cropped base64 data URL to a File object for API upload
      const byteString = atob(base64.split(',')[1])
      const mimeType = base64.split(',')[0].split(':')[1].split(';')[0]
      const ab = new ArrayBuffer(byteString.length)
      const ia = new Uint8Array(ab)
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i)
      }
      const blob = new Blob([ab], { type: mimeType })
      const file = new File([blob], 'featured_image.jpg', { type: mimeType })

      this.articleForm.featured_image = file
      this.imagePreview = base64

      this.showImageCropper = false
      this.imageCropperSrc = null
    },

    handleImageCropperCancel() {
      this.showImageCropper = false
      this.imageCropperSrc = null
    },

    removeImage() {
      this.articleForm.featured_image = null
      this.imagePreview = null
    }
  }
}
</script>

<style scoped>
/* Card Styles */
.card {
  border: none;
  border-radius: 15px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  border-radius: 15px 15px 0 0;
  padding: 1.5rem;
}

.card-title {
  font-weight: var(--font-weight-semibold);
}

.card-body {
  padding: 1.5rem;
}

/* Table Styles */
.table {
  margin-bottom: 0;
}

.table th {
  background-color: #f8f9fa;
  border-top: none;
  font-weight: var(--font-weight-semibold);
  color: #495057;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: rgba(26, 128, 144, 0.05);
}

/* Button Styles */
.btn-primary {
  background-color: #009CAD;
  border-color: #009CAD;
  font-weight: var(--font-weight-medium);
}

.btn-primary:hover {
  background-color: #009CAD;
  border-color: #009CAD;
}

.btn-outline-primary {
  color: #009CAD;
  border-color: #009CAD;
}

.btn-outline-primary:hover {
  background-color: #009CAD;
  border-color: #009CAD;
}

.btn-group-sm .btn {
  padding: 0.375rem 0.5rem;
}

/* Form Styles */
.form-control:focus {
  border-color: #009CAD;
  box-shadow: 0 0 0 0.2rem rgba(26, 128, 144, 0.25);
}

.form-check-input:checked {
  background-color: #009CAD;
  border-color: #009CAD;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  backdrop-filter: blur(3px);
}

.modal-container {
  background: white;
  border-radius: 15px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: modalSlideIn 0.3s ease-out;
}

.modal-container.modal-lg {
  max-width: 800px;
}

.modal-header {
  background-color: #009CAD;
  color: white;
  border-radius: 15px 15px 0 0;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-weight: var(--font-weight-semibold);
  margin: 0;
  font-size: var(--font-size-lg);
}

.close-btn {
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s ease;
  font-size: var(--font-size-lg);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 2rem;
  max-height: 60vh;
  overflow-y: auto;
}

.modal-footer {
  padding: 1.5rem 2rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* Form Groups */
.form-group {
  margin-bottom: 2rem;
}

.form-label {
  display: block;
  font-weight: var(--font-weight-semibold);
  color: #1e293b;
  margin-bottom: 0.75rem;
  font-size: var(--font-size-base);
}

.form-control {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: var(--font-size-base);
  font-family: inherit;
  transition: all 0.3s ease;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.form-control:focus {
  outline: none;
  border-color: #009CAD;
  box-shadow: 0 0 0 3px rgba(26, 128, 144, 0.1), 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #fefefe;
}

.form-control:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.form-control.is-invalid {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.invalid-feedback {
  color: #ef4444;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  margin-top: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.invalid-feedback::before {
  content: '⚠️';
  font-size: var(--font-size-xs);
}

.text-danger {
  color: #ef4444;
}

/* Button Styles */

.btn-secondary:hover {
  background-color: #e9ecef;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Badge Styles */
.badge {
  font-size: var(--font-size-xs);
  padding: 0.5rem 0.75rem;
}

/* Pagination */
.pagination .page-link {
  color: #009CAD;
  border-color: #dee2e6;
}

.pagination .page-item.active .page-link {
  background-color: #009CAD;
  border-color: #009CAD;
}

.pagination .page-link:hover {
  color: #0d5d6b;
  background-color: #e9ecef;
  border-color: #009CAD;
}

/* Loading States */
.spinner-border {
  color: #009CAD;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Input Group */
.input-group .btn {
  border-left: none;
}

.input-group .form-control:focus + .btn {
  border-color: #009CAD;
}

/* File Upload Styles */
.file-upload-area {
  position: relative;
  margin-bottom: 0.5rem;
}

.file-upload-content {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 3rem 2rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;
}

.file-upload-content::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(26, 128, 144, 0.05) 0%, rgba(26, 128, 144, 0.02) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.file-upload-content:hover,
.file-upload-content--drag {
  border-color: #009CAD;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(26, 128, 144, 0.15);
}

.file-upload-content:hover::before {
  opacity: 1;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  position: relative;
  z-index: 1;
}

.upload-icon {
  font-size: var(--font-size-4xl);
  color: #64748b;
  transition: all 0.3s ease;
}

.file-upload-content:hover .upload-icon {
  color: #009CAD;
  transform: scale(1.1);
}

.upload-text {
  font-weight: var(--font-weight-semibold);
  color: #1e293b;
  margin: 0;
  font-size: var(--font-size-md);
  transition: color 0.3s ease;
}

.file-upload-content:hover .upload-text {
  color: #009CAD;
}

.upload-subtext {
  font-size: var(--font-size-sm);
  color: #64748b;
  margin: 0;
  transition: color 0.3s ease;
}

.file-upload-content:hover .upload-subtext {
  color: #475569;
}

.uploaded-file {
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview {
  position: relative;
  display: inline-block;
}

.preview-image {
  width: 280px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.preview-image:hover {
  transform: scale(1.05);
}

.remove-image {
  position: absolute;
  top: -10px;
  right: -10px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  border: 2px solid white;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: var(--font-size-sm);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
  transition: all 0.3s ease;
}

.remove-image:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: scale(1.1);
  box-shadow: 0 6px 16px rgba(220, 38, 38, 0.6);
}

.file-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  color: #1e293b;
  background: rgba(255, 255, 255, 0.9);
  padding: 1rem 1.5rem;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.upload-hint {
  font-size: var(--font-size-sm);
  color: #94a3b8;
  margin: 0.5rem 0 0 0;
  font-style: italic;
  transition: color 0.3s ease;
}

.file-upload-content:hover .upload-hint {
  color: #64748b;
}

/* Toggle Switch Styles */
.form-check-container {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
}

.form-check-container:hover {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border-color: #009CAD;
  box-shadow: 0 4px 12px rgba(26, 128, 144, 0.1);
}

.toggle-switch {
  position: relative;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-label {
  display: inline-block;
  width: 56px;
  height: 32px;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-label::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(26, 128, 144, 0.1) 0%, rgba(26, 128, 144, 0.05) 100%);
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.toggle-slider {
  position: absolute;
  top: 4px;
  left: 4px;
  width: 24px;
  height: 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 50%;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15), 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.toggle-slider::after {
  content: '';
  width: 8px;
  height: 8px;
  background: #cbd5e1;
  border-radius: 50%;
  transition: all 0.3s ease;
}

.toggle-input:checked + .toggle-label {
  background: linear-gradient(135deg, #009CAD 0%, #0f6b75 100%);
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(26, 128, 144, 0.2);
}

.toggle-input:checked + .toggle-label::before {
  opacity: 1;
}

.toggle-input:checked + .toggle-label .toggle-slider {
  transform: translateX(24px);
  background: linear-gradient(135deg, #ffffff 0%, #f0f9ff 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2), 0 2px 4px rgba(0, 0, 0, 0.1);
}

.toggle-input:checked + .toggle-label .toggle-slider::after {
  background: #009CAD;
}

.toggle-text {
  font-weight: var(--font-weight-semibold);
  color: #1e293b;
  cursor: pointer;
  margin: 0;
  font-size: var(--font-size-base);
  transition: color 0.3s ease;
}

.form-check-container:hover .toggle-text {
  color: #009CAD;
}

/* Focus states for accessibility */
.toggle-input:focus + .toggle-label {
  outline: 2px solid #009CAD;
  outline-offset: 2px;
}
.me-2 {
  margin-right: 0.5rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mt-4 {
  margin-top: 1.5rem;
}

.py-5 {
  padding-top: 3rem;
  padding-bottom: 3rem;
}

.w-100 {
  width: 100%;
}

.d-flex {
  display: flex;
}

.gap-2 {
  gap: 0.5rem;
}

.align-items-center {
  align-items: center;
}

.justify-content-between {
  justify-content: space-between;
}

.justify-content-end {
  justify-content: flex-end;
}

.flex-column {
  flex-direction: column;
}

.text-center {
  text-align: center;
}

.text-muted {
  color: #6c757d;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* Animation */
@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Custom Scrollbar */
.modal-body::-webkit-scrollbar {
  width: 6px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #009CAD;
  border-radius: 3px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #0d5d6b;
}

/* Responsive Design */
@media (max-width: 768px) {
  .modal-overlay {
    padding: 0.5rem;
  }

  .modal-container {
    max-height: 95vh;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    padding: 1rem;
  }

  .modal-footer {
    flex-direction: column;
  }

  .btn {
    width: 100%;
  }

  .card-header .d-flex {
    flex-direction: column;
    gap: 1rem;
  }

  .btn-group {
    flex-wrap: wrap;
  }

  .table-responsive {
    font-size: var(--font-size-sm);
  }
}

@media (max-width: 576px) {
  .card-body {
    padding: 1rem;
  }

  .btn-sm {
    padding: 0.25rem 0.4rem;
    font-size: var(--font-size-sm);
  }
}
</style>
